import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { assign, values } from 'lodash';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { DocumentMeasurementsService } from '../document-measurement.service';
import { DocumentMeasurement } from '../document-measurement.types';

@Component({
    selector: 'document-measurement-details',
    templateUrl: './document-measurement-details.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentMeasurementDetailsComponent implements OnInit, OnDestroy {

    documentMeasurement: DocumentMeasurement;
    documentMeasurementForm: FormGroup;
    dialogTitle = `New Document Measurement`
    alert: any;
    reportType: string;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) private _data: {
            documentMeasurement: DocumentMeasurement,
            reportType: string
        },
        private documentMeasurementService: DocumentMeasurementsService,
        private _matDialogRef: MatDialogRef<DocumentMeasurementDetailsComponent>,
        private _formBuilder: FormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _alertMessage: AlertMessageService
    ) {
        this.reportType = this._data.reportType;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Open the drawer
        this.createDocumentForm();

        if (this._data.documentMeasurement.id) {
            // Request the data from the server
            this.documentMeasurementService.getDocumentMeasurement(this._data.documentMeasurement.id)
                .subscribe((res) => {
                    if (res.isSuccess) {
                        this.documentMeasurement = res.result;
                        this.dialogTitle = `Edit Document Measurement`;
                        this.documentMeasurementForm.patchValue(this.documentMeasurement);
                    }
                    else {
                        this._alertMessage.error(res.errors.join('\n'));
                        this.onClose();
                    }
                });
            // Update card when there is a value change on the card form
            // this.documentMeasurementForm.valueChanges
            //     .pipe(
            //         tap((value) => {
            //             // Update the card object
            //             this.documentMeasurement = assign(this.documentMeasurement, value);
            //         }),
            //         debounceTime(10000),
            //         takeUntil(this._unsubscribeAll)
            //     )
            //     .subscribe((value) => {
            //         // Update the card on the server
            //         this.documentMeasurementService.updateDocumentMeasurement(value).subscribe((response) => {
            //             if (response.isSuccess) {
            //                 this.alert = {
            //                     type: 'success',
            //                     message: 'Auto save successfully!'
            //                 };

            //                 setTimeout(() => {
            //                     this.alert = null;
            //                 }, 7000);
            //             }
            //         });

            //         // Mark for check
            //         this._changeDetectorRef.markForCheck();
            //     });
        }
    }

    createDocumentForm(): void {
        this.documentMeasurementForm = this._formBuilder.group({
            id: [''],
            reportId: [this._data.documentMeasurement.reportId],
            flatPlotNo: [this._data.documentMeasurement.flatPlotNo, [Validators.required]],
            carpetArea: [this._data.documentMeasurement.carpetArea.toFixed(2), [Validators.pattern(/^\d*(?:[.,]\d{1,2})?$/)]],
            builtUpArea: [this._data.documentMeasurement.builtUpArea.toFixed(2)],
            superBuiltUpArea: [this._data.documentMeasurement.superBuiltUpArea.toFixed(2)],
            orderIndex: [this._data.documentMeasurement.orderIndex],
        });

    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    setResetForm(oldData: DocumentMeasurement) {

        this._data.documentMeasurement as DocumentMeasurement;
        this._data.documentMeasurement.reportId = oldData.reportId;
        this._data.documentMeasurement.orderIndex = oldData.orderIndex + 1;

        this.documentMeasurementForm.reset();
        this.documentMeasurementForm.patchValue(this._data.documentMeasurement);
    }

    saveAndNew() {
        const documentMeasurement = this.documentMeasurementForm.getRawValue();
        this.save(documentMeasurement, true);
    }

    saveAndClose() {
        const documentMeasurement = this.documentMeasurementForm.getRawValue();
        this.save(documentMeasurement);
    }

    updateAndClose() {

        const documentMeasurement = this.documentMeasurementForm.getRawValue();

        this.documentMeasurementService.updateDocumentMeasurement(documentMeasurement).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("Document Measurement updated successfully");
                // Close the dialog
                this._matDialogRef.close(response);
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });
    }

    save(documentMeasurement, isOpen = false) {
        this.documentMeasurementService.createDocumentMeasurement(documentMeasurement).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("Document measurement added successfully");
                // Close the dialog
                if (!isOpen) {
                    this._matDialogRef.close(response);
                }
                else {
                    this.setResetForm(documentMeasurement);
                }
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });
    }

    onClose(): void {
        this._matDialogRef.close();
    }
}
